import { API_URL, OFFERS_API_URL } from './constants'

/** Only public offer reads retry. Signing and transaction POSTs never use this helper. */
export class OfferReadError extends Error {
	constructor(readonly status: number, readonly code: string) {
		super('Offers are temporarily unavailable.')
		this.name = 'OfferReadError'
	}
}

export async function readOffers<T>(path: string, base = API_URL): Promise<T> {
	for (let attempt = 0; ; attempt++) {
		const controller = new AbortController()
		const timeout = setTimeout(() => controller.abort(), 8_000)
		let retryable = true
		let retryAfter = 0
		try {
			const response = await fetch(`${base}${path}${base === OFFERS_API_URL && base !== API_URL ? `${path.includes('?') ? '&' : '?'}t=${Date.now()}` : ''}`, {
				cache: 'no-store',
				signal: controller.signal
			})
			// Deploy proxies can return HTML. Preserve the HTTP status instead of
			// exposing a JSON parsing error, and do not retry permanent 4xx errors.
			retryable = response.status === 408 || response.status === 429 || response.status >= 500
			retryAfter = Math.min(
				5_000,
				Math.max(0, Number(response.headers?.get('retry-after')) * 1000 || 0)
			)
			const data = await response.json().catch(() => null)
			if (!response.ok || !data || data.error) {
				throw new OfferReadError(response.status, data?.code || `http_${response.status}`)
			}
			return data as T
		} catch (error) {
			if (!retryable || attempt >= 2) throw error
		} finally {
			clearTimeout(timeout)
		}
		await new Promise((resolve) => setTimeout(resolve, Math.max(retryAfter, [750, 1500][attempt])))
	}
}
